// Hook custom per aggiungere una visualizzazione di un articolo specifico tramite API.
// Nota: uso axios e TypeScript per tipizzare la risposta, gestire errori, loading e refetch.

import { useState, useCallback } from 'react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

// Tipizzazione Parametri dell'hook
interface hookParams {
    id: number              // ID dell'articolo da recuperare
    agencyId: number;       // ID dell'agenzia
    enabled?: boolean;      // se abilitare la chiamata (utile per componenti condizionali)
}

// Tipizzazione Risultato dell'hook
interface useAddArticleViewResult {
    message: string | null;
    actualViews: number | null;
    error: string | null;
    addArticleView: () => Promise<void>;
}

/**
 * useAddArticleView
 * @param id - ID dell'articolo
 * @param agencyId - ID dell'agenzia
 * @returns - Oggetto con message avvenuta visualizzazione, n° visualizzazioni ed possibile errors
*/

const useAddArticleView = ({
    id,
    agencyId,
    enabled = false,
}: hookParams): useAddArticleViewResult => {
    const [message, setMessage] = useState<string | null>(null);
    const [actualViews, setActualViews] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Funzione fetch interna con useCallback per evitare ricreazioni
    const addArticleView = useCallback(async () => {
        if (!enabled) {
            return;
        }

        try{
            const response = await axios.post(`${API_BASE_URL}/blog/articolo/detail/add-view`, {
                id,
                agencyId,
            });
            setMessage(response.data.message);
            setActualViews(response.data.actualViews);
            setError(null);
            
        } catch (err: unknown) {
            if (axios.isAxiosError(err)) {
                // err è tipizzato come AxiosError
                const errorMessage = err.response?.data?.error || err.message;
                setError(errorMessage);
            } else if (err instanceof Error) {
                // err è un normale Error di JS
                setError(err.message);
            } else {
                // fallback generico
                setError("Errore sconosciuto");
            }
        }
    }, [id, agencyId, enabled]);

    return {
        message,
        actualViews,
        error,
        addArticleView
    }
}

export default useAddArticleView;
